/**
 * UI scale. Laptop-sized viewports open at a reduced scale so the
 * dashboard and panels fit without the user reaching for Ctrl+-.
 *
 * Pure model + detection only — no DOM writes and no storage, both of which live
 * in UserPreferencesService.
 */

export enum UiScale {
  /** Pick from the viewport at load. */
  Auto = 'auto',
  Scaled = 'scaled',
  Normal = 'normal',
}

/** Matches the browser-zoom-80% view the ticket asked to make the default. */
export const HELIOS_UI_SCALE = 0.8

/**
 * Thresholds are CSS px, which fold in OS display scaling and browser zoom — a
 * 1080p laptop at 125% Windows scaling reports ~1536 wide and is caught, a 1080p
 * monitor reports 1920 and is not, and someone who already zoomed out reports
 * wider and is correctly left alone.
 *
 * Width does the real work. The height clause only catches wide-but-short
 * windows, and 825px keeps it clear of a 1080p monitor at ~871px with a bookmarks
 * bar and one more toolbar.
 */
export function detectPrefersScaled(win: Window = window): boolean {
  // jsdom omits matchMedia, and this is reached from UserPreferencesService's
  // field initialiser — throwing here would break app bootstrap, so an
  // environment that cannot answer the question gets the unscaled default.
  if (typeof win.matchMedia !== 'function') return false

  return win.matchMedia('(max-width: 1600px), (max-height: 825px)').matches
}

/**
 * Guards the boundary where an untyped localStorage string becomes a `UiScale`.
 * Without it a non-member value binds to nothing in the drawer's control, and
 * `resolveScale` treats it as `Auto` — so an explicit `Scaled` silently becomes
 * viewport-dependent.
 *
 * The `as string[]` cast is required: `Object.values(UiScale).includes(value)`
 * does not compile against a non-member string.
 */
export function isUiScale(value: string | null): value is UiScale {
  return (Object.values(UiScale) as string[]).includes(value ?? '')
}

/** The multiplier to apply, consulting the viewport only for `Auto`. */
export function resolveScale(pref: UiScale, win: Window = window): number {
  if (pref === UiScale.Normal) return 1
  if (pref === UiScale.Scaled) return HELIOS_UI_SCALE

  return detectPrefersScaled(win) ? HELIOS_UI_SCALE : 1
}
